import debug from 'debug';
import { getUserId } from 'sdi/app';
import { icon } from 'sdi/components/button';
import { DIV, INPUT, H1, H2, BUTTON } from 'sdi/components/elements';
import { renderCheckbox } from 'sdi/components/input';
import tr, { formatNumber, fromRecord, Translated } from 'sdi/locale';
import { markdown } from 'sdi/ports/marked';
import { Category } from 'sdi/source';
import { notEmpty } from 'sdi/util';
import {
    getMapsInCategory,
    getMode,
    getSelectedCategories,
    getSelectedRoles,
    getSelectedStatus,
    getVisibleCategories,
    getVisibleRoles,
} from 'view/src/queries/mapnavigator';
import {
    filterMapsWithPat,
    toggleHomeMode,
    removeFilterMapsCat,
    addFilterMapsCat,
    addFilterMapsRole,
    removeFilterMapsRole,
    addFilterMapsStatus,
    clearFilterMapsStatus,
} from '../../events/mapnavigator';
import { renderSwitchToTableButton, renderSwitchToTileButton } from '../button';
import { renderRoleColor } from '../mapnavigator';

const logger = debug('sdi:sidebar');

// const clearCategorySelection = makeLabelAndIcon('clear', 3, 'times', () =>
//     tr.core('unselect')
// );

const searchInput = () =>
    INPUT({
        key: 'map-navigator-search-input',
        type: 'search',
        name: 'search',
        placeholder: tr.view('searchAtlas'),
        onChange: e => filterMapsWithPat(e.currentTarget.value.trim()),
    });

const searchButton = () => icon('search');

export const searchField = () =>
    DIV('input-wrapper', searchInput(), searchButton());

const isSelectedCategory = (catId: string) =>
    getSelectedCategories().indexOf(catId) >= 0;
const isSelectedRole = (roleId: string) => getSelectedRoles().includes(roleId);

const isSelectedStatus = (status: string) => getSelectedStatus() == status;

const isSelectedCatClass = (catId: string) =>
    isSelectedCategory(catId) ? 'selected' : 'unselected';
const isSelectedRoleClass = (fcatId: string) =>
    isSelectedRole(fcatId) ? 'selected' : 'unselected';
const isSelectedStatusClass = (status: string) =>
    isSelectedStatus(status) ? 'selected' : 'unselected';

const catCheckBox = (c: Category) =>
    renderCheckbox(
        'categories',
        () =>
            `${fromRecord(c.name)} (${formatNumber(
                getMapsInCategory(c.id).length,
            )})` as Translated,
        b => (b ? addFilterMapsCat(c.id) : removeFilterMapsCat(c.id)),
    );

const renderCategoryList = (cats: Category[] | readonly Category[]) =>
    cats.map(c => {
        return catCheckBox(c)(
            isSelectedCategory(c.id),
            isSelectedCatClass(c.id),
        );
    });

// To get back when tooltips will be fixed with the scrollbar
// const renderRoleDescription = (role: Category) =>
//     nonEmptyStr(fromRecord(role.description)).map(description =>
//         DETAILS(
//             'download-table',
//             SUMMARY('btn btn-3 icon-only', icon('info')),
//             DIV('download__body', description),
//         ),
//     );

const renderRoleList = (roles: Category[] | readonly Category[]) =>
    DIV(
        'category__wrapper__content role-list',
        ...roles.map(role =>
            DIV(
                {
                    className: 'role-picker__container',
                    title: fromRecord(role.description),
                },
                DIV(
                    {
                        className: `role-picker ${role} ${isSelectedRoleClass(
                            role.id,
                        )}`,
                    },
                    renderRoleColor(role),
                    BUTTON(
                        {
                            className: `label`,
                            onClick: () => {
                                !isSelectedRole(role.id)
                                    ? addFilterMapsRole(role.id)
                                    : removeFilterMapsRole(role.id);
                            },
                        },
                        fromRecord(role.name),
                    ),
                    // renderRoleDescription(role),
                ),
            ),
        ),
    );

const renderStatusList = () =>
    DIV(
        'category__wrapper__element',
        H2('', tr.view('statusFilter')),
        DIV(
            'category__wrapper__content status-list',
            BUTTON(
                {
                    className: `view-status ${isSelectedStatusClass('public_published')}`,
                    onClick: () =>
                        !isSelectedStatus('public_published')
                            ? addFilterMapsStatus('public_published')
                            : clearFilterMapsStatus(),
                },
                DIV('view-status__legend public_published', icon('check')),
                DIV('view-status__label', tr.core('public_published')),
            ),
            BUTTON(
                {
                    className: `view-status ${isSelectedStatusClass('internally_published')}`,
                    onClick: () =>
                        !isSelectedStatus('internally_published')
                            ? addFilterMapsStatus('internally_published')
                            : clearFilterMapsStatus(),
                },
                DIV(
                    'view-status__legend internally_published',
                    icon('user-plus'),
                ),
                DIV('view-status__label', tr.core('internally_published')),
            ),
            BUTTON(
                {
                    className: `view-status ${isSelectedStatusClass('private')}`,
                    onClick: () =>
                        !isSelectedStatus('private')
                            ? addFilterMapsStatus('private')
                            : clearFilterMapsStatus(),
                },
                DIV('view-status__legend private', icon('lock')),
                DIV('view-status__label', tr.core('private')),
            ),
        ),
    );

const renderSwitch = () =>
    DIV(
        'sidebar-switch',
        getUserId().map(() =>
            getMode() === 'tile'
                ? renderSwitchToTableButton(toggleHomeMode)
                : renderSwitchToTileButton(toggleHomeMode),
        ),
    );

const mobileMediaQuery =
    'screen and (max-width: 900px) and (orientation: portrait)';

export const isMobile = () => window.matchMedia(mobileMediaQuery).matches;

// const renderCategoryListWrapper = () =>
//     notEmpty(getVisibleCategories()).map(bcats =>
//         isMobile()
//             ? DETAILS(
//                   '',
//                   SUMMARY('', H2('', tr.view('filterByCategory'))),
//                   renderCategoryList(bcats)
//               )
//             : DIV(
//                   '',
//                   H2('', tr.view('filterByCategory')),
//                   renderCategoryList(bcats)
//               )
//     );

export const renderSidebar = () =>
    DIV(
        'sidebar',
        H1('pitch', markdown(tr.view('pitchAtlas'))),
        H2('pitch sub-pitch', markdown(tr.view('subPitchAtlas'))),
        DIV(
            'category__wrapper',
            getUserId().map(() => renderStatusList()),
            notEmpty(getVisibleCategories()).map(bcats =>
                DIV(
                    'category__wrapper__element',
                    H2('', tr.view('filterByCategory')),
                    DIV(
                        'category__wrapper__content',
                        renderCategoryList(bcats),
                    ),
                ),
            ),
            notEmpty(getVisibleRoles()).map(fcats =>
                DIV(
                    'category__wrapper__element',
                    H2('', tr.view('roleFilter')),
                    renderRoleList(fcats),
                ),
            ),
        ),
        renderSwitch(),
        DIV('sidebar-footer', markdown(tr.view('dataAccess'))),
    );

export default renderSidebar;

logger('loaded');
